import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Input } from "@agentnative/ui";
import { AuthLayout, authInputClass, authButtonClass } from "../components/AuthLayout";
import { resetPassword, validateResetToken, fetchCsrfToken } from "../lib/api/auth";
import { apiErrorMessage } from "../lib/api/client";

// Keep in step with the server's only hard rule (services/auth/password.go,
// minPasswordLength). This is early feedback; the server is the authority.
const MIN_PASSWORD_LENGTH = 8;

type TokenState = "checking" | "valid" | "invalid";

// Step 2 of a password reset: the page the emailed link lands on.
//
// The token is read from the URL once and then removed from the address bar,
// so it does not linger in browser history or leak through a Referer header or
// a screenshot. It is held in memory only.
export function ResetPasswordRoute() {
  const tokenRef = useRef<string>("");
  const [tokenState, setTokenState] = useState<TokenState>("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    tokenRef.current = params.get("token") ?? "";
    if (params.has("token")) {
      window.history.replaceState(null, "", window.location.pathname);
    }
    if (!tokenRef.current) {
      setTokenState("invalid");
      return;
    }
    validateResetToken(tokenRef.current)
      .then((r) => setTokenState(r.valid ? "valid" : "invalid"))
      .catch(() => setTokenState("invalid"));
  }, []);

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const mismatch = confirm.length > 0 && password !== confirm;

  const mutation = useMutation({
    mutationFn: async () => {
      await fetchCsrfToken();
      return resetPassword({ token: tokenRef.current, newPassword: password });
    },
    onSuccess: (resp) => {
      if (resp.success) {
        tokenRef.current = "";
        setPassword("");
        setConfirm("");
        setDone(true);
      }
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (tooShort || mismatch || !password) return;
    mutation.mutate();
  }

  const failedMessage = mutation.isSuccess && !mutation.data.success ? mutation.data.message : null;

  return (
    <AuthLayout title="Choose a new password" subtitle="Set a new password for your Agent Native account">
      <div className="flex flex-col gap-4">
        {tokenState === "checking" && <p className="font-sans text-sm">Checking your link...</p>}

        {tokenState === "invalid" && !done && (
          <>
            <Alert variant="danger" title="This link is not valid">
              The reset link is invalid, has expired, or has already been used. Request a new one.
            </Alert>
            <a href="/forgot-password">
              <Button variant="primary" className={authButtonClass}>Request a new link</Button>
            </a>
          </>
        )}

        {done && (
          <>
            <Alert variant="success" title="Password updated">
              Your password has been changed. Sign in with your new password.
            </Alert>
            <a href="/login">
              <Button variant="primary" className={authButtonClass}>Go to sign in</Button>
            </a>
          </>
        )}

        {tokenState === "valid" && !done && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {mutation.isError && (
              <Alert variant="danger" title="Could not reset password">
                {apiErrorMessage(mutation.error)}
              </Alert>
            )}
            {failedMessage && (
              <Alert variant="danger" title="Could not reset password">
                {failedMessage}
              </Alert>
            )}
            <Input
              className={authInputClass}
              label="New password"
              id="reset-password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              error={tooShort ? `At least ${MIN_PASSWORD_LENGTH} characters` : undefined}
            />
            <Input
              className={authInputClass}
              label="Confirm new password"
              id="reset-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirm(e.target.value)}
              error={mismatch ? "Passwords do not match" : undefined}
            />
            <Button
              type="submit"
              variant="primary"
              className={authButtonClass}
              disabled={mutation.isPending || !password || tooShort || mismatch || confirm.length === 0}
            >
              {mutation.isPending ? "Saving..." : "Set new password"}
            </Button>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
